import { coerce, eq, gt, gte, lt, lte } from 'semver';
import {
  DeviceUtils,
  TvModelYear,
  VersionUtils,
  deviceUtilsDefault
} from './types';

const getSamsungVersion = (): VersionUtils => {
  const userAgentString = navigator.userAgent;

  if (!userAgentString.toLowerCase().includes('tizen')) {
    return deviceUtilsDefault.version;
  }

  const tizenVersion = userAgentString.match(/Tizen ([0-9\\.]+)/i);
  const semver = coerce(tizenVersion?.[1]);

  if (!semver) return deviceUtilsDefault.version;

  return {
    semver,
    equal: (v: string) => eq(semver, v),
    greaterThan: (v: string) => gt(semver, v),
    greaterThanOrEqual: (v: string) => gte(semver, v),
    lessThan: (v: string) => lt(semver, v),
    lessThanOrEqual: (v: string) => lte(semver, v)
  };
};

const yearModelMap: {
  year: TvModelYear;
  test: (v: VersionUtils) => boolean;
}[] = [
  {
    year: '2015',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('2.0.0') && v.lessThan('2.4.0')
  },
  {
    year: '2016',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('2.4.0') && v.lessThan('3.0.0')
  },
  {
    year: '2017',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('3.0.0') && v.lessThan('4.0.0')
  },
  {
    year: '2018',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('4.0.0') && v.lessThan('5.0.0')
  },
  {
    year: '2019',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('5.0.0') && v.lessThan('5.5.0')
  },
  {
    year: '2020',
    test: (v: VersionUtils) => v.greaterThan('5.0.0') && v.lessThan('6.0.0')
  },
  {
    year: '2021',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('6.0.0') && v.lessThan('6.5.0')
  },
  {
    year: '2022',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('6.5.0') && v.lessThan('7.0.0')
  },
  {
    year: '2023',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('7.0.0') && v.lessThan('8.0.0')
  },
  {
    year: '2024',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('8.0.0') && v.lessThan('9.0.0')
  },
  {
    year: '2025',
    test: (v: VersionUtils) =>
      v.greaterThanOrEqual('9.0.0') && v.lessThan('10.0.0')
  }
];

export const samsungUtils: DeviceUtils = {
  get platform() {
    return 'samsung' as const;
  },
  get version() {
    return getSamsungVersion();
  },
  get isDiscontinued() {
    return this.version.lessThanOrEqual('5.0.0');
  },
  get isModern() {
    return this.version.greaterThanOrEqual('6.0.0');
  },
  get isLowEnd() {
    return this.version.lessThan('3.0.0');
  },
  get modelYear() {
    const version = this.version;
    return yearModelMap.filter((m) => m.test(version)).at(0)?.year ?? 'UNKNOWN';
  },
  get isMouseSupported() {
    return false;
  },
  get isScrollSupported() {
    return false;
  }
};
